import React, { useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
// Types
import { FilterProps } from 'pages/PackSite/types/packsiteLog';
// Utils
import { isEmptyObject, useSetState, getSessionStorageItem } from 'utils';
// Auth Context
import { useAppDispatch } from 'context/AppContext';
import { useAuthState } from 'context/AuthContext';
// Event Context
import { usePackSiteDispatch, usePackSiteState } from 'context/PackSiteContext';
import { setPackDetailFilters } from 'context/PackSiteContext/actions';
import { getProdDataDetails } from 'context/PackSiteContext/middleware';
// Elements
import Button from 'elements/Button';
import DateDropdown from 'elements/DateDropdown';
import { SearchField } from 'elements/Forms';
// Components
import PackSiteFilter from './PackSiteFilter';
// Style
import styles from './style/packsiteFilterBar.module.css';

interface PackSiteFilterBarProps {
  display?:boolean,
  setDisabled: Function,
}

function PackSiteFilterBar({ display, setDisabled }: PackSiteFilterBarProps) {
  // App State
  const appDispatch = useAppDispatch();
  // const { defaultSite: { orgRef } } = useAuthState();
  const { defaultOrganisation: { orgRef } } = useAuthState();
  // Event State
  const { packDetailFilters } = usePackSiteState();
  const packSitesDispatch = usePackSiteDispatch();
  // State
  const defaultState = useMemo(() => ({
    limit: '100',
    offSet: 0,
    supplierId: '',
    MFGItem: '',
    SMSitemId: '',
    RMSitemId: '',
    customerId: '',
    operatingUnit: getSessionStorageItem({ item: 'defaultOrganisation' }).orgDescription,
    packSite: '',
    itemDescription: '',
    dateEffectiveFrom: '',
    dateEffectiveTo: '',
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
  const [state, setState] = useSetState(isEmptyObject(packDetailFilters) ? defaultState : packDetailFilters);

  const getData = useCallback(async (filters:FilterProps) => {
    if (packDetailFilters.offSet !== filters.offSet || filters.offSet === 0) {
      await getProdDataDetails({
        appDispatch,
        packSitesDispatch,
        filters,
        orgRef,
      });
      setDisabled(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packSitesDispatch]);

  async function handleClear() {
    packSitesDispatch(setPackDetailFilters(defaultState));
    setState(defaultState);
    // await getData(defaultState);
  }

  async function handleUpdate() {
    packSitesDispatch(setPackDetailFilters(state));
    // await getData(state);
  }

  const handleUpdateAuto = useCallback(async (filters:FilterProps) => {
    packSitesDispatch(setPackDetailFilters(filters));
  }, [packSitesDispatch]);

  /* useEffect(() => {
    if (!isEmptyObject(packDetailFilters)) {
      getData(packDetailFilters);
    } else {
      getData(defaultState);
    }
    return function cleanup() {
      // packSitesDispatch(setPackDetailFilters(defaultState));
      packSitesDispatch(setPackSites([]));
      packSitesDispatch(setPackSiteState(''));
    };
  }, [defaultState, packSitesDispatch, packDetailFilters, getData]); */

  useEffect(() => {
    if (!isEmptyObject(packDetailFilters) && packDetailFilters.operatingUnit === defaultState.operatingUnit) {
      getData(packDetailFilters);
      setState(packDetailFilters);
    }  else {
      handleUpdateAuto(defaultState);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultState, packDetailFilters, getData, handleUpdateAuto]);

  return (
    <>
      { display ?
        <div className={styles.container}>
          <div className={styles.filters}>
            <PackSiteFilter
              label="Effective Date"
              value={
            (state.dateEffectiveFrom)
              ? `${state.dateEffectiveFrom} ${state.dateEffectiveTo ? '-' : ''} ${state.dateEffectiveTo || ''}`
              : 'Select'
          }
            >
          
              <div className={styles.dates}>
                <div>
                  <DateDropdown
                    dateFormat="DD MMM YYYY"
                    isClearable={false}
                    maxDate={null}
                    minDate={null}
                    onChange={(date:string) => setState({
                      dateEffectiveFrom: moment(date).format('DD MMM YYYY'),
                    })}
                    placeholder="From Date"
                    selected={state.dateEffectiveFrom ? moment(state.dateEffectiveFrom) : null}
                    value=""
                  />
                </div>
                <div>
                  <DateDropdown
                    dateFormat="DD MMM YYYY"
                    isClearable={false}
                    minDate={state.dateEffectiveFrom ? moment(state.dateEffectiveFrom).add(1, 'day') : null}
                    onChange={(date:string) => setState({
                      dateEffectiveTo: moment(date).format('DD MMM YYYY'),
                    })}
                    placeholder="To Date"
                    selected={state.dateEffectiveTo ? moment(state.dateEffectiveTo) : null}
                    value=""
                  />
                </div>
              </div>
            </PackSiteFilter>
            <SearchField
              icon={false}
              keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ RMSitemId: e.target.value })}
              onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ RMSitemId: e.target.value })}
              onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ RMSitemId: e.target.value })}
              placeholderText="Product Identification No"
              value={state.RMSitemId}
            />
            <SearchField
              icon={false}
              keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ MFGItem: e.target.value })}
              onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ MFGItem: e.target.value })}
              onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ MFGItem: e.target.value })}
              placeholderText="EBS Item"
              value={state.MFGItem}
            />
            <SearchField
              icon={false}
              keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ itemDescription: e.target.value })}
              onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ itemDescription: e.target.value })}
              onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ itemDescription: e.target.value })}
              placeholderText="Product Description"
              value={state.itemDescription}
            />
            <SearchField
              icon={false}
              keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ customerId: e.target.value })}
              onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ customerId: e.target.value })}
              onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ customerId: e.target.value })}
              placeholderText="Distribution Depot ID"
              value={state.customerId}
            />
            <SearchField
              icon={false}
              keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ packSite: e.target.value })}
              onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ packSite: e.target.value })}
              onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ packSite: e.target.value })}
              placeholderText="Pack Site"
              value={state.packSite}
            />
          </div>
          <div className={styles.actions}>
            <Button
              btnStyle="light"
              disabled={JSON.stringify(state) === JSON.stringify(defaultState)}
              handler={() => handleClear()}
            >
              Clear
            </Button>
            <Button
              btnStyle="secondary"
              handler={() => handleUpdate()}
            >
              Update
            </Button>
          </div>
        </div> : ''}
    </>
  );
}

export default PackSiteFilterBar;


PackSiteFilterBar.defaultProps = {
  display: true,
};